import { createApp, h } from 'vue'
import NewTask from '../components/projects/tasks/New.vue'
import TaskShow from '../components/projects/tasks/Show.vue'
import ProjectShow from '../components/projects/Show.vue'
import Dashboard from '../components/dashboard/Dashboard.vue'
import Profile from '../components/profile/Profile.vue'
import Setup from '../components/setup/Setup.vue'
import ProjectNew from '../components/projects/New.vue'
import ProjectSetup from '../components/projects/ProjectSetup.vue'
import AccessDenied from '../components/common/AccessDenied.vue'
import Calendar from '../components/calendar/Calendar.vue'
import BoardShow from '../components/boards/Show.vue'
import ProjectDashboard from '../components/projects/dashboard/ProjectDashboard.vue'
import ReportConstructor from '../components/reports/Constructor.vue'
import PublicAttachmentPage from '../components/public/AttachmentPage.vue'
import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client/core'
import { DefaultApolloClient } from '@vue/apollo-composable'
import i18nPlugin from '../plugins/i18n'

// Shared styles (typography, forms, modals, text selection)
import '../styles/fonts.css'
import '../styles/forms.css'
import '../styles/modals.css'
import '../styles/text-selection.css'
import '../styles/rich-content-read.css'
import '../styles/task-preview-table.css'

const TELEGRAM_WEB_APP_REMOTE_SCRIPT_SRC = 'https://telegram.org/js/telegram-web-app.js'
const TELEGRAM_WEB_APP_LOCAL_SCRIPT_SRC = '/vendor/telegram-web-app.js'
const TELEGRAM_WEB_APP_REMOTE_TIMEOUT_MS = 1200
let telegramWebAppScriptPromise = null
const IS_TEST_ENV =
  typeof document !== 'undefined' && document.documentElement?.dataset?.railsEnv === 'test'

function getTelegramWebApp() {
  return (typeof window !== 'undefined' && window.Telegram && window.Telegram.WebApp) || null
}

function tryTelegramReadyAndExpand() {
  const tgWebApp = getTelegramWebApp()
  if (!tgWebApp) return

  try {
    if (typeof tgWebApp.ready === 'function') {
      tgWebApp.ready()
    }
    if (typeof tgWebApp.expand === 'function') {
      tgWebApp.expand()
    }
  } catch (e) {
    console.warn('Failed to call Telegram WebApp.ready() or expand():', e)
  }
}

function injectTelegramWebAppScriptCode(code) {
  if (typeof document === 'undefined') return false
  if (getTelegramWebApp()) return true

  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.text = code

  const target = document.head || document.documentElement
  if (!target) {
    return false
  }

  target.appendChild(script)
  return !!getTelegramWebApp()
}

async function fetchTelegramWebAppScriptWithTimeout(url, timeoutMs) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-cache',
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    return await response.text()
  } finally {
    clearTimeout(timeoutId)
  }
}

function loadTelegramWebAppScript(src) {
  if (getTelegramWebApp()) {
    return Promise.resolve(true)
  }

  return new Promise(resolve => {
    if (typeof document === 'undefined') {
      resolve(false)
      return
    }

    const existingScript = document.querySelector(`script[src="${src}"]`)
    if (existingScript) {
      if (getTelegramWebApp()) {
        resolve(true)
        return
      }
      existingScript.addEventListener('load', () => resolve(!!getTelegramWebApp()), { once: true })
      existingScript.addEventListener('error', () => resolve(false), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.defer = true
    script.onload = () => resolve(!!getTelegramWebApp())
    script.onerror = () => resolve(false)

    const target = document.head || document.documentElement
    if (!target) {
      resolve(false)
      return
    }

    target.appendChild(script)
  })
}

function ensureTelegramWebAppScriptLoaded() {
  if (IS_TEST_ENV) {
    return Promise.resolve(false)
  }

  if (getTelegramWebApp()) {
    return Promise.resolve(true)
  }

  if (telegramWebAppScriptPromise) {
    return telegramWebAppScriptPromise
  }

  telegramWebAppScriptPromise = (async () => {
    try {
      const remoteScriptCode = await fetchTelegramWebAppScriptWithTimeout(
        TELEGRAM_WEB_APP_REMOTE_SCRIPT_SRC,
        TELEGRAM_WEB_APP_REMOTE_TIMEOUT_MS
      )
      const remoteLoaded = injectTelegramWebAppScriptCode(remoteScriptCode)
      if (remoteLoaded && getTelegramWebApp()) {
        return true
      }
      console.warn(
        'Telegram WebApp SDK remote code executed but WebApp is unavailable, using local fallback'
      )
    } catch (error) {
      if (error && error.name === 'AbortError') {
        console.warn(
          `Telegram WebApp SDK remote load timed out after ${TELEGRAM_WEB_APP_REMOTE_TIMEOUT_MS}ms, aborted request and using local fallback`
        )
      } else {
        console.warn('Telegram WebApp SDK remote load failed, using local fallback')
      }
    }

    if (getTelegramWebApp()) {
      return true
    }

    const localLoaded = await loadTelegramWebAppScript(TELEGRAM_WEB_APP_LOCAL_SCRIPT_SRC)
    if (!localLoaded) {
      console.warn('Telegram WebApp SDK local fallback failed to load')
    }

    return localLoaded && !!getTelegramWebApp()
  })()

  return telegramWebAppScriptPromise
}

function mountNewTask() {
  const el = document.getElementById('new-task')
  if (!el) return
  const props = {
    projectId: el.dataset.projectId || null,
    chatId: el.dataset.chatId || null,
    fromId: el.dataset.fromId || null,
  }
  const app = createApp({
    render: () => h(NewTask, props),
  })
  app.mount(el)
}

function buildApolloClient(fromId) {
  // Ensure Telegram WebApp is ready before accessing initData
  const tgWebApp = getTelegramWebApp()
  if (tgWebApp) {
    try {
      if (tgWebApp.ready) {
        tgWebApp.ready()
      }
      // Expand to fullscreen mode
      if (tgWebApp.expand) {
        tgWebApp.expand()
      }
      // Configure WebApp settings after ready() is called
      configureTelegramWebApp()
    } catch (e) {
      console.warn('Failed to call Telegram WebApp.ready() or expand():', e)
    }
  }

  const tgUser = (tgWebApp && tgWebApp.initDataUnsafe && tgWebApp.initDataUnsafe.user) || null
  const tgUserId = tgUser && tgUser.id
  const viewerId = tgUserId || fromId || null
  const viewerUsername = tgUser && tgUser.username
  const viewerName = tgUser ? [tgUser.first_name, tgUser.last_name].filter(Boolean).join(' ') : null
  const viewerLanguageCode = tgUser && tgUser.language_code

  // Detect browser timezone using Intl API
  const viewerTimezone =
    typeof Intl !== 'undefined' && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : null

  // Get Telegram initData for secure authentication (if available)
  // According to Telegram Mini Apps docs: https://docs.telegram-mini-apps.com/platform/authorizing-user
  // initData might not be available immediately, so we need to wait for it
  // It's available as window.Telegram.WebApp.initData (raw query string)
  let tgInitData = null
  if (tgWebApp) {
    // Try to get initData - it might be available after WebApp.ready() is called
    tgInitData = tgWebApp.initData || null

    // If not available, try to get it from the URL or wait a bit
    if (!tgInitData && tgWebApp.initDataUnsafe) {
      // initDataUnsafe is available but we need the raw initData string
      // Unfortunately, Telegram doesn't expose the raw string directly in all cases
      // We'll need to rely on the backend receiving it via headers
    }
  }

  // Helper function to check if a string contains only ISO-8859-1 characters
  // ISO-8859-1 supports characters with code points 0x00-0xFF (0-255)
  const isISO88591 = str => {
    if (!str) return true
    try {
      // Check if all characters have code points within ISO-8859-1 range (0-255)
      for (let i = 0; i < str.length; i++) {
        const codePoint = str.codePointAt(i)
        if (codePoint > 0xff) {
          return false
        }
      }
      return true
    } catch {
      return false
    }
  }

  // Helper function to safely set header only if value is ISO-8859-1 compatible
  const safeHeader = (key, value) => {
    if (!value) return {}
    // Check if value contains non-ISO-8859-1 characters
    if (isISO88591(value)) {
      return { [key]: value }
    }
    // Skip header if it contains non-ISO-8859-1 characters to avoid fetch error
    console.warn(`Skipping header ${key} due to non-ISO-8859-1 characters`)
    return {}
  }

  const link = createHttpLink({
    uri: '/graphql',
    headers: {
      // Send initData in Authorization header as per Telegram Mini Apps spec:
      // Format: "tma <initDataRaw>" where "tma" is the auth type
      ...(tgInitData ? { Authorization: `tma ${tgInitData}` } : {}),
      // Also send in custom header for backward compatibility
      ...(tgInitData ? { 'X-Telegram-Init-Data': tgInitData } : {}),
      // Legacy headers for backward compatibility (less secure, will be removed)
      // Only include headers with ISO-8859-1 compatible values
      ...(viewerId ? { 'X-Debug-Telegram-User-Id': String(viewerId) } : {}),
      ...safeHeader('X-Telegram-User-Username', viewerUsername),
      ...safeHeader('X-Telegram-User-Name', viewerName),
      ...safeHeader('X-Telegram-User-Language-Code', viewerLanguageCode),
      ...safeHeader('X-Telegram-User-Timezone', viewerTimezone),
    },
    credentials: 'include', // Include cookies (for session token)
  })
  return new ApolloClient({ link, cache: new InMemoryCache() })
}

// Function to apply safe area insets to CSS
function applySafeAreaInset() {
  if (
    typeof window === 'undefined' ||
    !window.Telegram ||
    !window.Telegram.WebApp ||
    !document.documentElement
  ) {
    return
  }

  const tgWebApp = window.Telegram.WebApp

  try {
    // Get safe area insets from Telegram WebApp API
    if (tgWebApp.safeAreaInset) {
      const safeArea = tgWebApp.safeAreaInset
      // Apply as CSS custom properties
      document.documentElement.style.setProperty('--safe-area-inset-top', `${safeArea.top || 0}px`)
      document.documentElement.style.setProperty(
        '--safe-area-inset-bottom',
        `${safeArea.bottom || 0}px`
      )
    }

    // Also use contentSafeAreaInset if available (for content area)
    if (tgWebApp.contentSafeAreaInset) {
      const contentSafeArea = tgWebApp.contentSafeAreaInset
      document.documentElement.style.setProperty(
        '--content-safe-area-inset-top',
        `${contentSafeArea.top || 0}px`
      )
      document.documentElement.style.setProperty(
        '--content-safe-area-inset-bottom',
        `${contentSafeArea.bottom || 0}px`
      )
    }
  } catch (e) {
    console.warn('Failed to apply safe area inset:', e)
  }
}

// Function to determine if back button should be visible on current page
function shouldShowBackButton() {
  // Don't show back button on Dashboard (main entry point)
  if (document.getElementById('dashboard')) {
    return false
  }
  // Show back button on other pages
  return true
}

// Function to get stored task index URL from sessionStorage
function getStoredTaskIndexUrl(projectId) {
  try {
    return sessionStorage.getItem(`taskIndexUrl_${projectId}`)
  } catch (e) {
    console.warn('Failed to get stored task index URL:', e)
  }
  return null
}

// Function to handle back button click
function handleBackButtonClick() {
  const currentUrl = new URL(window.location.href)
  const pathname = currentUrl.pathname
  const searchParams = currentUrl.searchParams

  // Determine where to navigate based on current page
  if (pathname.includes('/tasks/') && pathname.match(/\/tasks\/\d+$/)) {
    // Task Show page -> go back to Task Index
    const taskIdMatch = pathname.match(/\/tasks\/(\d+)$/)
    const projectIdMatch = pathname.match(/\/projects\/([^/]+)\/tasks\/\d+$/)
    if (projectIdMatch) {
      const projectId = projectIdMatch[1]

      // Try to restore the stored task index URL (with filters)
      const storedUrl = getStoredTaskIndexUrl(projectId)
      if (storedUrl) {
        // Parse the stored URL and preserve chat_id/from_id from current page if needed
        const storedUrlObj = new URL(storedUrl, window.location.origin)
        if (searchParams.has('chat_id'))
          storedUrlObj.searchParams.set('chat_id', searchParams.get('chat_id'))
        if (searchParams.has('from_id'))
          storedUrlObj.searchParams.set('from_id', searchParams.get('from_id'))
        window.location.href = storedUrlObj.pathname + storedUrlObj.search
      } else {
        // No stored URL, go to project dashboard
        let backUrl = `/projects/${projectId}/dashboard`
        if (searchParams.has('chat_id') || searchParams.has('from_id')) {
          const url = new URL(backUrl, window.location.origin)
          if (searchParams.has('chat_id'))
            url.searchParams.set('chat_id', searchParams.get('chat_id'))
          if (searchParams.has('from_id'))
            url.searchParams.set('from_id', searchParams.get('from_id'))
          backUrl = url.pathname + url.search
        }
        window.location.href = backUrl
      }
      return
    }
  } else if (pathname.includes('/tasks/new')) {
    // Task New page -> go back to Task Index
    const projectIdMatch = pathname.match(/\/projects\/([^/]+)\/tasks\/new$/)
    if (projectIdMatch) {
      const projectId = projectIdMatch[1]

      // Try to restore the stored task index URL (with filters)
      const storedUrl = getStoredTaskIndexUrl(projectId)
      if (storedUrl) {
        // Parse the stored URL and preserve chat_id/from_id from current page if needed
        const storedUrlObj = new URL(storedUrl, window.location.origin)
        if (searchParams.has('chat_id'))
          storedUrlObj.searchParams.set('chat_id', searchParams.get('chat_id'))
        if (searchParams.has('from_id'))
          storedUrlObj.searchParams.set('from_id', searchParams.get('from_id'))
        window.location.href = storedUrlObj.pathname + storedUrlObj.search
      } else {
        // No stored URL, go to project dashboard
        let backUrl = `/projects/${projectId}/dashboard`
        if (searchParams.has('chat_id') || searchParams.has('from_id')) {
          const url = new URL(backUrl, window.location.origin)
          if (searchParams.has('chat_id'))
            url.searchParams.set('chat_id', searchParams.get('chat_id'))
          if (searchParams.has('from_id'))
            url.searchParams.set('from_id', searchParams.get('from_id'))
          backUrl = url.pathname + url.search
        }
        window.location.href = backUrl
      }
      return
    }
  } else if (pathname.includes('/projects/') && pathname.match(/\/projects\/[^/]+\/settings$/)) {
    // Project Settings page -> go back to Task Index
    const projectIdMatch = pathname.match(/\/projects\/([^/]+)\/settings$/)
    if (projectIdMatch) {
      const projectId = projectIdMatch[1]

      // Try to restore the stored task index URL (with filters)
      const storedUrl = getStoredTaskIndexUrl(projectId)
      if (storedUrl) {
        // Parse the stored URL and preserve chat_id/from_id from current page if needed
        const storedUrlObj = new URL(storedUrl, window.location.origin)
        if (searchParams.has('chat_id'))
          storedUrlObj.searchParams.set('chat_id', searchParams.get('chat_id'))
        if (searchParams.has('from_id'))
          storedUrlObj.searchParams.set('from_id', searchParams.get('from_id'))
        window.location.href = storedUrlObj.pathname + storedUrlObj.search
      } else {
        // No stored URL, go to project dashboard
        let backUrl = `/projects/${projectId}/dashboard`
        if (searchParams.has('chat_id') || searchParams.has('from_id')) {
          const url = new URL(backUrl, window.location.origin)
          if (searchParams.has('chat_id'))
            url.searchParams.set('chat_id', searchParams.get('chat_id'))
          if (searchParams.has('from_id'))
            url.searchParams.set('from_id', searchParams.get('from_id'))
          backUrl = url.pathname + url.search
        }
        window.location.href = backUrl
      }
      return
    }
  } else if (pathname.includes('/personal_projects/new')) {
    // Personal Project New page -> go back to Dashboard
    let backUrl = '/dashboard'
    // Preserve chat_id and from_id
    if (searchParams.has('chat_id') || searchParams.has('from_id')) {
      const url = new URL(backUrl, window.location.origin)
      if (searchParams.has('chat_id')) url.searchParams.set('chat_id', searchParams.get('chat_id'))
      if (searchParams.has('from_id')) url.searchParams.set('from_id', searchParams.get('from_id'))
      backUrl = url.pathname + url.search
    }
    window.location.href = backUrl
    return
  } else if (pathname === '/projects/new') {
    // Project New page -> go back to Dashboard
    let backUrl = '/dashboard'
    // Preserve chat_id and from_id
    if (searchParams.has('chat_id') || searchParams.has('from_id')) {
      const url = new URL(backUrl, window.location.origin)
      if (searchParams.has('chat_id')) url.searchParams.set('chat_id', searchParams.get('chat_id'))
      if (searchParams.has('from_id')) url.searchParams.set('from_id', searchParams.get('from_id'))
      backUrl = url.pathname + url.search
    }
    window.location.href = backUrl
    return
  }

  // Default: go back to Dashboard
  let backUrl = '/dashboard'
  // Preserve chat_id and from_id
  if (searchParams.has('chat_id') || searchParams.has('from_id')) {
    const url = new URL(backUrl, window.location.origin)
    if (searchParams.has('chat_id')) url.searchParams.set('chat_id', searchParams.get('chat_id'))
    if (searchParams.has('from_id')) url.searchParams.set('from_id', searchParams.get('from_id'))
    backUrl = url.pathname + url.search
  }
  window.location.href = backUrl
}

// Function to configure Telegram WebApp settings
function configureTelegramWebApp() {
  if (typeof window === 'undefined' || !window.Telegram || !window.Telegram.WebApp) {
    return
  }

  const tgWebApp = window.Telegram.WebApp

  try {
    // Ensure WebApp is ready before configuring
    if (typeof tgWebApp.ready === 'function') {
      tgWebApp.ready()
    }

    // Expand to fullscreen mode (important for desktop and mobile)
    // web_app buttons open in compact mode by default, so we need to expand them
    // This should be called early to ensure full width is available
    if (typeof tgWebApp.expand === 'function') {
      tgWebApp.expand()
      // Also check if we're in compact mode and expand if needed
      // The viewportHeight property indicates if we're in compact mode
      if (tgWebApp.viewportHeight && tgWebApp.viewportStableHeight) {
        // If viewportHeight is significantly less than viewportStableHeight, we're in compact mode
        const isCompact = tgWebApp.viewportHeight < tgWebApp.viewportStableHeight * 0.9
        if (isCompact) {
          tgWebApp.expand()
        }
      }
    }

    // Set header color (for the top bar in compact mode)
    if (typeof tgWebApp.setHeaderColor === 'function') {
      tgWebApp.setHeaderColor('#eee')
    }

    // Set background color (for the area behind the mini-app)
    if (typeof tgWebApp.setBackgroundColor === 'function') {
      tgWebApp.setBackgroundColor('#f7f7f8') // Match app background color
    }

    // Disable vertical swipes to prevent swipe-to-dismiss behavior
    if (typeof tgWebApp.disableVerticalSwipes === 'function') {
      tgWebApp.disableVerticalSwipes()
    }

    // Apply safe area insets
    applySafeAreaInset()

    // Don't setup back button - we want to keep the Close button visible
    // setupBackButton()

    // Listen for safe area changes (e.g., when device rotates or safe area changes)
    if (tgWebApp.onEvent) {
      tgWebApp.onEvent('safeAreaChanged', () => {
        applySafeAreaInset()
      })
      tgWebApp.onEvent('contentSafeAreaChanged', () => {
        applySafeAreaInset()
      })
      // Listen for viewport changes and ensure full screen mode
      // This is important for web_app buttons which might open in compact mode
      tgWebApp.onEvent('viewportChanged', () => {
        // Ensure we're in full screen mode when viewport changes
        if (typeof tgWebApp.expand === 'function') {
          tgWebApp.expand()
        }
      })
    }
  } catch (e) {
    console.warn('Failed to configure Telegram WebApp settings:', e)
  }
}

// Function to add Telegram WebApp class to body
// Only adds the class if the app is actually running inside Telegram (not just if SDK is loaded)
function addTelegramWebAppClass() {
  if (typeof window === 'undefined' || !document.body) {
    return
  }

  const tgWebApp = window.Telegram && window.Telegram.WebApp
  if (!tgWebApp) {
    return
  }

  // Only add class if we're actually inside Telegram (initData is available)
  // When opened in a regular browser, initData won't be available even if SDK is loaded
  const isInsideTelegram = tgWebApp.initData && tgWebApp.initData.length > 0
  if (isInsideTelegram) {
    document.body.classList.add('telegram-webapp')
  }
}

// Try immediately (if body exists)
addTelegramWebAppClass()
// Configure Telegram WebApp immediately when script loads
// This ensures expand() is called as early as possible for full-width mode
configureTelegramWebApp()
tryTelegramReadyAndExpand()

// Load Telegram SDK asynchronously without blocking app bootstrap.
// If it loads later, configure Telegram-specific behavior at that point.
void ensureTelegramWebAppScriptLoaded().then(isLoaded => {
  if (!isLoaded) {
    console.warn(
      'Telegram WebApp SDK failed to load; continuing without Telegram-specific features'
    )
    return
  }

  addTelegramWebAppClass()
  configureTelegramWebApp()
  tryTelegramReadyAndExpand()
})

document.addEventListener('DOMContentLoaded', () => {
  // Ensure Telegram SDK load is started (does not block app startup)
  void ensureTelegramWebAppScriptLoaded()

  // Ensure Telegram WebApp class is set
  addTelegramWebAppClass()
  // Configure WebApp settings
  configureTelegramWebApp()

  // Also ensure expand() is called on DOMContentLoaded (in case WebApp is already available)
  tryTelegramReadyAndExpand()

  // Don't setup back button - we want to keep the Close button visible
  // setupBackButton()

  // Check if we need to reload with correct from_id for user's locale
  const tgWebApp = getTelegramWebApp()
  const tgUser = (tgWebApp && tgWebApp.initDataUnsafe && tgWebApp.initDataUnsafe.user) || null
  const tgUserId = tgUser && tgUser.id
  const currentUrl = new URL(window.location.href)
  const fromIdInUrl = currentUrl.searchParams.get('from_id')

  // If we have a Telegram user ID and it doesn't match from_id in URL, reload with correct from_id
  if (tgUserId && fromIdInUrl !== String(tgUserId)) {
    currentUrl.searchParams.set('from_id', tgUserId)
    window.location.href = currentUrl.toString()
    return // Stop execution, page will reload
  }

  // New Task mount
  const newEl = document.getElementById('new-task')
  if (newEl) {
    const projectId = newEl.dataset.projectId
    if (!projectId || projectId.trim() === '') {
      console.error('new-task: projectId is missing or empty in data-project-id attribute')
    }
    const props = {
      projectId: projectId || null,
      chatId: newEl.dataset.chatId || null,
      fromId: newEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(NewTask, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    // Ensure Telegram WebApp is ready so initData is populated
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(newEl)
  }

  const showEl = document.getElementById('task-show')
  if (showEl) {
    const props = {
      projectId: showEl.dataset.projectId || null,
      taskId: showEl.dataset.taskIid || null,
      taskGlobalId: showEl.dataset.taskId || null,
      chatId: showEl.dataset.chatId || null,
      fromId: showEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(TaskShow, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(showEl)
  }

  const dashEl = document.getElementById('dashboard')
  if (dashEl) {
    const props = {
      chatId: dashEl.dataset.chatId || null,
      fromId: dashEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(Dashboard, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(dashEl)
  }

  const projectEl = document.getElementById('project-show')
  if (projectEl) {
    const props = {
      projectId: projectEl.dataset.projectId || null,
      chatId: projectEl.dataset.chatId || null,
      fromId: projectEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(ProjectShow, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(projectEl)
  }

  const profileEl = document.getElementById('profile')
  if (profileEl) {
    const props = {
      chatId: profileEl.dataset.chatId || null,
      fromId: profileEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(Profile, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(profileEl)
  }

  const setupEl = document.getElementById('setup')
  if (setupEl) {
    const props = {
      chatId: setupEl.dataset.chatId || null,
      fromId: setupEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(Setup, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(setupEl)
  }

  const personalProjectNewEl = document.getElementById('personal-project-new')
  if (personalProjectNewEl) {
    const props = {
      chatId: personalProjectNewEl.dataset.chatId || null,
      fromId: personalProjectNewEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(ProjectNew, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(personalProjectNewEl)
  }

  const projectNewEl = document.getElementById('project-new')
  if (projectNewEl) {
    const props = {
      chatId: projectNewEl.dataset.chatId || null,
      fromId: projectNewEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(ProjectNew, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(projectNewEl)
  }

  const calendarEl = document.getElementById('calendar')
  if (calendarEl) {
    const props = {
      chatId: calendarEl.dataset.chatId || null,
      fromId: calendarEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(Calendar, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(calendarEl)
  }

  const boardShowEl = document.getElementById('board-show')
  if (boardShowEl) {
    const boardId = boardShowEl.dataset.boardId
    if (!boardId || boardId.trim() === '') {
      console.error('board-show: boardId is missing or empty in data-board-id attribute')
      return
    }
    const props = {
      boardId: boardId,
      projectId: boardShowEl.dataset.projectId || null,
      chatId: boardShowEl.dataset.chatId || null,
      fromId: boardShowEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(BoardShow, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(boardShowEl)
  }

  const projectSetupEl = document.getElementById('project-setup')
  if (projectSetupEl) {
    const projectId = projectSetupEl.dataset.projectId
    if (!projectId || projectId.trim() === '') {
      console.error('project-setup: projectId is missing or empty in data-project-id attribute')
      return
    }
    const props = {
      projectId: projectId || null,
      chatId: projectSetupEl.dataset.chatId || null,
      fromId: projectSetupEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(ProjectSetup, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(projectSetupEl)
  }

  const projectDashboardEl = document.getElementById('project-dashboard')
  if (projectDashboardEl) {
    const props = {
      projectId: projectDashboardEl.dataset.projectId || null,
      chatId: projectDashboardEl.dataset.chatId || null,
      fromId: projectDashboardEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(ProjectDashboard, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(projectDashboardEl)
  }

  const reportConstructorEl = document.getElementById('report-constructor')
  if (reportConstructorEl) {
    const props = {
      projectId: reportConstructorEl.dataset.projectId || null,
      chatId: reportConstructorEl.dataset.chatId || null,
      fromId: reportConstructorEl.dataset.fromId || null,
    }
    const app = createApp({
      render: () => h(ReportConstructor, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(reportConstructorEl)
  }

  const accessDeniedEl = document.getElementById('access-denied')
  if (accessDeniedEl) {
    const props = {
      projectId: accessDeniedEl.dataset.projectId || null,
      projectTitle: accessDeniedEl.dataset.projectTitle || null,
      chatId: accessDeniedEl.dataset.chatId || null,
      fromId: accessDeniedEl.dataset.fromId || null,
      reason: accessDeniedEl.dataset.reason || null,
    }
    const app = createApp({
      render: () => h(AccessDenied, props),
    })
    app.use(i18nPlugin)
    const apollo = buildApolloClient(props.fromId)
    try {
      window.Telegram &&
        window.Telegram.WebApp &&
        window.Telegram.WebApp.ready &&
        window.Telegram.WebApp.ready()
    } catch {}
    app.provide(DefaultApolloClient, apollo)
    app.mount(accessDeniedEl)
  }

  const publicAttachmentEl = document.getElementById('public-attachment-page')
  if (publicAttachmentEl) {
    const props = JSON.parse(publicAttachmentEl.dataset.props || '{}')
    const app = createApp({
      render: () => h(PublicAttachmentPage, props),
    })
    app.mount(publicAttachmentEl)
  }
})
